import React, { useCallback, useContext, useEffect, useState } from 'react';
import Drawer from '@src/components/wysiwyg_editor/toolbox/Drawer';
import classnames from 'classnames';
import useChangeLayout from '@src/hooks/wysiwyg_editor/useChangeLayout';
import Header from '../drawer_headers/Layout';
import useIsLargeScreen from '@src/hooks/useIsLargeScreen';
import { PageContentStateContext } from '@src/contexts/wysiwyg/page_content';
import {
  WysiwygEditorDispatchContext,
  WysiwygEditorStateContext
} from '@src/contexts/wysiwyg/editor';
import Notification from '@src/components/modal/Notification';
import Text from '@src/components/modal/Text';
import { $fontSize9, $fontWeightBold } from '@src/variables';
import { isImage, isTextBox } from '@src/utils/wysiwyg_editor/data';

declare global {
  interface Window {
    layoutsNotificationModalShown?: boolean;
  }
}

type TOptionsItem = {
  src: string;
  alt: string;
  name: string;
  selected: boolean;
  onClick: () => void;
};

interface OptionItemData {
  src: string;
  alt: string;
  name: string;
}

const smallScreenWarning =
  'You are about to change the selected layout. If you continue, you ' +
  'will lose any changes you have made so far. Would you like to ' +
  'continue?';

const optionItems = {
  designed_layouts: {
    photo: [
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/1 photo layout.svg'),
        alt: '1 photo layout',
        name: 'stencil_1_photo_v1'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/2 photo layout.svg'),
        alt: '2 photo layout',
        name: 'stencil_2_photo_v1'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/2 photo layout (alt).svg'),
        alt: '2 photo layout (alt)',
        name: 'stencil_2_photo_v2'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/3 photo layout.svg'),
        alt: '3 photo layout',
        name: 'stencil_3_photo_v1'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/3 photo layout (alt).svg'),
        alt: '3 photo layout (alt)',
        name: 'stencil_3_photo_v2'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/3 photo layout (alt v3).svg'),
        alt: '3 photo layout (alt v3)',
        name: 'stencil_3_photo_v3'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/4 photo layout (alt v3).svg'),
        alt: '4 photo layout (alt v3)',
        name: 'stencil_4_photo_v3'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/4 photo layout.svg'),
        alt: '4 photo layout',
        name: 'stencil_4_photo_v1'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/4 photo layout (alt).svg'),
        alt: '4 photo layout (alt)',
        name: 'stencil_4_photo_v2'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/5 photo layout.svg'),
        alt: '5 photo layout',
        name: 'stencil_5_photo_v1'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/6 photo layout.svg'),
        alt: '6 photo layout',
        name: 'stencil_6_photo_v1'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/9 photo layout.svg'),
        alt: '9 photo layout',
        name: 'stencil_9_photo_v1'
      }
    ],
    text: [
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/1_photo_text_v1.svg'),
        alt: '1 photo layout w/ text',
        name: 'stencil_1_photo_text_v1'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/2_photo_text_v1.svg'),
        alt: '2 photo layout w/ text',
        name: 'stencil_2_photo_text_v1'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/2_photo_text_v2.svg'),
        alt: '2 photo layout w/ text (var. 2)',
        name: 'stencil_2_photo_text_v2'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/2_photo_text_v3.svg'),
        alt: '2 photo layout w/ text (var. 3)',
        name: 'stencil_2_photo_text_v3'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/3_photo_text_v1.svg'),
        alt: '3 photo layout w/ text',
        name: 'stencil_3_photo_text_v1'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/3_photo_text_v2.svg'),
        alt: '3 photo layout w/ text (var. 2)',
        name: 'stencil_3_photo_text_v2'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/4_photo_text_v1.svg'),
        alt: '4 photo layout w/ text',
        name: 'stencil_4_photo_text_v1'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/4_photo_text_v2.svg'),
        alt: '4 photo layout w/ text (var. 2)',
        name: 'stencil_4_photo_text_v2'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/designed_layouts/6_photo_text_v1.svg'),
        alt: '6 photo layout w/ text',
        name: 'stencil_6_photo_text_v1'
      }
    ]
  },
  cover_designer: {
    layouts: [
      {
        src: require('@images/wysiwyg_editor/layout_icons/cover_designer/layout_1.svg'),
        alt: 'layout 1',
        name: 'cover_layout_1'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/cover_designer/layout_2.svg'),
        alt: 'layout 2',
        name: 'cover_layout_2'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/cover_designer/layout_3.svg'),
        alt: 'layout 3',
        name: 'cover_layout_3'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/cover_designer/layout_4.svg'),
        alt: 'layout 4',
        name: 'cover_layout_4'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/cover_designer/layout_5.svg'),
        alt: 'layout 5',
        name: 'cover_layout_5'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/cover_designer/layout_6.svg'),
        alt: 'layout 6',
        name: 'cover_layout_6'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/cover_designer/layout_7.svg'),
        alt: 'layout 7',
        name: 'cover_layout_7'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/cover_designer/layout_8.svg'),
        alt: 'layout 8',
        name: 'cover_layout_8'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/cover_designer/layout_9.svg'),
        alt: 'layout 9',
        name: 'cover_layout_9'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/cover_designer/layout_10.svg'),
        alt: 'layout 10',
        name: 'cover_layout_10'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/cover_designer/layout_11.svg'),
        alt: 'layout 11',
        name: 'cover_layout_11'
      },
      {
        src: require('@images/wysiwyg_editor/layout_icons/cover_designer/layout_12.svg'),
        alt: 'layout 12',
        name: 'cover_layout_12'
      }
    ]
  }
};

const optionGroupLabels = {
  cover_designer: {
    layouts: 'Layouts'
  },
  designed_layouts: {
    photo: 'Photos Only',
    text: 'Photo w/ Text'
  }
};

const DEFAULT_TEMPLATE = {
  designed_layouts: 'stencil_1_photo_v1',
  cover_designer: 'cover_layout_1'
};

function StencilLayoutOptionsItem({
  src,
  alt,
  name,
  selected,
  onClick
}: TOptionsItem) {
  return (
    <button
      className={classnames('wysiwyg-editor-layouts-options-item', {
        'wysiwyg-editor-layouts-options-item--selected': selected
      })}
      data-layout-name={name}
      type="button"
      onClick={onClick}
    >
      <img src={src} alt={alt} />
    </button>
  );
}

function Layout(): React.ReactElement {
  const isLarge = useIsLargeScreen();
  const { layoutTemplateName: appliedLayout } = useContext(
    PageContentStateContext
  );
  const { contentType } = useContext(WysiwygEditorStateContext);
  const { closeToolbox } = useContext(WysiwygEditorDispatchContext);
  const { contentItems } = useContext(PageContentStateContext);
  const imageItemsWithContent = contentItems
    .filter(isImage)
    .filter((image) => image.href);
  const textItemsWithContent = contentItems
    .filter(isTextBox)
    .filter((text) => text.content);

  const changeLayout = useChangeLayout();
  const [selectedLayout, setSelectedLayout] = useState(
    appliedLayout || DEFAULT_TEMPLATE[contentType]
  );
  const [showModal, setShowModal] = useState(false);

  const handleLayoutClick = useCallback(
    (layoutName) => {
      const shouldContinue =
        isLarge ||
        !(imageItemsWithContent.length || textItemsWithContent.length) ||
        confirm(smallScreenWarning);

      if (!shouldContinue) return;

      changeLayout(layoutName);
      if (isLarge) {
        if (
          textItemsWithContent.length &&
          !window.layoutsNotificationModalShown
        )
          setShowModal(true);
      } else {
        closeToolbox();
      }
    },
    [
      changeLayout,
      closeToolbox,
      imageItemsWithContent,
      isLarge,
      textItemsWithContent
    ]
  );

  useEffect(() => {
    setSelectedLayout(appliedLayout);
  }, [appliedLayout]);

  return (
    <Drawer header={Header}>
      {Object.keys(optionItems[contentType]).map((key) => {
        return (
          <section
            className="wysiwyg-editor-layouts-options"
            key={`options-group-${key}`}
          >
            <h2 className="wysiwyg-editor-layouts-options--header">
              {(optionGroupLabels[contentType] as Record<string, string>)[key]}
            </h2>
            <div className="wysiwyg-editor-layouts-options--items">
              {(optionItems[contentType] as Record<
                string,
                Array<OptionItemData>
              >)[key].map((item) => {
                return (
                  <StencilLayoutOptionsItem
                    src={item.src}
                    alt={item.alt}
                    name={item.name}
                    selected={selectedLayout === item.name}
                    onClick={() => handleLayoutClick(item.name)}
                    key={item.name}
                  />
                );
              })}
            </div>
          </section>
        );
      })}
      {showModal ? (
        <Notification
          contentStyles={{ padding: '2.5rem 2.8rem' }}
          modalStyles={{ maxWidth: '61.5rem' }}
          onClose={() => {
            setShowModal(false);
            window.layoutsNotificationModalShown = true;

            if (!isLarge) closeToolbox();
          }}
        >
          <Text
            align="center"
            style={{ fontSize: $fontSize9, fontWeight: $fontWeightBold }}
          >
            Your text content will be available in the Text Tab for use in other
            Layouts
          </Text>
        </Notification>
      ) : null}
    </Drawer>
  );
}

export default Layout;
